<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :items-per-page="10"
    class="elevation-1"
    :options.sync="options"
    item-key="id"
    :loading="loading"
    loading-text="Loading... Please wait"
  >
    <template v-slot:body="{ items: pageItems, headers: slotHeaders }">
      <tbody v-if="loading">
        <tr>
          <td :colspan="slotHeaders.length" class="text-center pa-10">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <div class="mt-4">Loading... Please wait</div>
          </td>
        </tr>
      </tbody>

      <tbody v-else-if="!pageItems || pageItems.length === 0">
        <tr>
          <td :colspan="slotHeaders.length" class="text-center pa-5">
            No data available
          </td>
        </tr>
      </tbody>

      <draggable
        v-else
        :list="pageItems"
        tag="tbody"
        handle=".drag-handle"
        @end="onDragEnd(pageItems)"
        :animation="300"
        ghostClass="ghost-row"
        dragClass="dragging-row"
        :scroll="true"
        :scrollSensitivity="100"
        :forceFallback="false"
      >
        <tr
          v-for="item in pageItems"
          :key="item.id"
          :class="[
            { 'selected-item': selectedIdsProxy.includes(item.id) },
            'hover-rows'
          ]"
          :style="rowStyle(item)"
        >
          <td
            v-for="h in slotHeaders"
            :key="h.value"
            class="text-center"
          >
            <!-- Drag + checkbox -->
            <template v-if="h.value === 'drag'">
              <div class="d-flex gap-2 align-center">
                <v-checkbox
                  v-model="selectedIdsProxy"
                  :value="item.id"
                  hide-details
                  class="mt-0 pt-0 mr-2"
                  v-if="item.marked_at === null"
                  @click.stop
                />
                <v-icon class="drag-handle" style="cursor: grab;">mdi-drag</v-icon>
              </div>
            </template>

            <!-- Basic info -->
            <template v-else-if="h.value === 'so_id'">
              <div class="d-flex flex-column gap-2">
                <div v-if="item.salesOrder">
                  <router-link :to="`/sales/salesorders/${item.salesOrder.id}`" target="_blank">
                    {{ item.salesOrder.so_number }}
                  </router-link>
                </div>
                <div v-else-if="item.sales_order">
                  <router-link :to="`/sales/salesorders/${item.sales_order.id}`" target="_blank">
                    {{ item.sales_order.so_number }}
                  </router-link>
                </div>

                <div class="d-flex flex-column gap-1">
                  <span>{{ item.company_name }}</span>
                  <span>{{ item.customer_name }}</span>
                  <span>{{ item.customer_email }}</span>

                  <span>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">PN:</span>
                      </template>
                      <span>Phone Number</span>
                    </v-tooltip>
                    {{ item.customer_phone }}
                  </span>
                </div>

                <div>
                  <span>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">TN:</span>
                      </template>
                      <span>Tax Number</span>
                    </v-tooltip>
                    {{ item.tax_number }}
                  </span>

                  <div>{{ item.billing_address.billing.postal_code }} {{ item.billing_address.billing.city }},</div>
                  <div>{{ item.billing_address.billing.address }}</div>
                  <div>{{ formatCountry(item.billing_address.billing.country) }}</div>
                </div>
              </div>
            </template>

            <!-- Shipping -->
            <template v-else-if="h.value === 'shipping_address.shipping.postal_code'">
              <template v-if="hasShipping(item)">
                <div>{{ shippingLine1(item) }}</div>
                <div>{{ item.shipping_address.shipping.address }}</div>
                <div>{{ formatCountry(item.shipping_address.shipping.country) }}</div>
              </template>
              <template v-else>
                <span style="font-style: italic; color: gray;">Personal pickup</span>
              </template>
            </template>

            <!-- Items cell -->
            <template v-else-if="h.value === 'salesOrder.so_number'">
              <v-icon
                @click="editText(item)"
                class="edit-button"
                v-if="item.id !== isEditingText"
              >
                mdi-pencil
              </v-icon>

              <div v-if="isEditingText && item.id === isEditingText" class="h-100">
                <v-textarea
                  height="100%"
                  hide-details
                  solo
                  label="Text"
                  v-model="editingText"
                  auto-grow
                  :value="editingText"
                  clearable
                ></v-textarea>

                <div class="d-flex gap-2 textarea-buttons">
                  <v-icon class="check-button" @click.stop="saveText(item.id)">mdi-check</v-icon>
                  <v-icon class="cancel-button" @click.stop="cancelTextEdit()">mdi-window-close</v-icon>
                </div>
              </div>

              <div v-else>
                <template v-if="item.items_text">
                  <div
                    class="items-text"
                    :class="['item-tr', { expanded: expandedItems[item.id] }]"
                    :ref="`items-${item.id}`"
                  >
                    {{ item.items_text }}
                  </div>

                  <div v-if="item?.meta?.delivery_date && !item.salesOrder && !item.sales_order">
                    <span :style="isDueWithinDays(item, 5) ? { color: 'red' } : {}">
                      Delivery date: {{ item?.meta?.delivery_date }}
                    </span>
                  </div>

                  <div v-if="item.salesOrder" class="mt-1">
                    <router-link :to="`/sales/salesorders/${item.salesOrder.id}`" target="_blank">
                      {{ item.salesOrder.so_number }}
                    </router-link>
                    <div v-if="item?.meta?.delivery_date">
                      <span :style="isDueWithinDays(item, 5) ? { color: 'red' } : {}">
                        Delivery date: {{ item?.meta?.delivery_date }}
                      </span>
                    </div>
                  </div>

                  <div v-else-if="item.sales_order" class="mt-1">
                    <router-link :to="`/sales/salesorders/${item.sales_order.id}`" target="_blank">
                      {{ item.sales_order.so_number }}
                    </router-link>
                    <div v-if="item?.meta?.delivery_date">
                      <span :style="isDueWithinDays(item, 5) ? { color: 'red' } : {}">
                        Delivery date: {{ item?.meta?.delivery_date }}
                      </span>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div
                    :class="[
                      'd-flex justify-space-between flex-column item-tr items-text',
                      { expanded: expandedItems[item.id] }
                    ]"
                    :ref="`items-${item.id}`"
                  >
                    <div v-for="it in item.items" :key="it.id">
                      <div>
                        {{ it.qty }} db - {{ it.reference }} - {{ it.item_name }} -
                        {{ it.default_qty ?? item.default_qty ?? 'N/A' }}
                        {{ it.unit ?? 'db' }}
                      </div>
                    </div>
                  </div>
                  <div v-if="item.salesOrder" class="mt-1">
                    <router-link :to="`/sales/salesorders/${item.salesOrder.id}`" target="_blank">
                      {{ item.salesOrder.so_number }}
                    </router-link>
                    <div v-if="item?.meta?.delivery_date">
                      <span :style="isDueWithinDays(item, 5) ? { color: 'red' } : {}">
                        Delivery date: {{ item?.meta?.delivery_date }}
                      </span>
                    </div>
                  </div>
                  
                  <div v-else-if="item.sales_order" class="mt-1">
                    <router-link :to="`/sales/salesorders/${item.sales_order.id}`" target="_blank">
                      {{ item.sales_order.so_number }}
                    </router-link>
                    <div v-if="item?.meta?.delivery_date">
                      <span :style="isDueWithinDays(item, 5) ? { color: 'red' } : {}">
                        Delivery date: {{ item?.meta?.delivery_date }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </template>

            <!-- Order date -->
            <template v-else-if="h.value === 'order_date'">
              {{ formatDate(item.order_date) }}
            </template>

            <!-- Admin -->
            <template v-else-if="h.value === 'user_id'">
              <v-tooltip bottom v-if="item.user">
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs" class="administrator">
                    {{ item.user.christian_name1 }}
                  </span>
                </template>
                <span>
                  {{ item.user.email }} - {{ item.user.surname }}
                  {{ item.user.christian_name1 }} {{ item.user.christian_name2 ?? '' }}
                </span>
              </v-tooltip>
            </template>

            <!-- Comment -->
            <template v-else-if="h.value === 'comment'">
              <div class="comment-text">
                {{ item.comment }}
              </div>
            </template>

            <!-- Actions -->
            <template v-else-if="h.value === 'actions'">
              <div class="d-flex flex-column gap-2">
                <div style="width: 100%; height: 24px;">
                    <v-icon
                      @click="toggleExpand(item.id)"
                      class="expand-icon"
                      v-if="itemsWithOverflow[item.id]"
                    >
                      {{ expandedItems[item.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>

                    <v-icon
                      @click="$emit('start-edit', item)"
                      class="expand-icon edit-button"
                    >
                      mdi-pencil
                    </v-icon>
                </div>

                <div>
                  <v-menu
                    offset-x
                    left
                    :close-on-content-click="false"
                    v-model="colorMenus[item.id]"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        class="justify-center"
                        dark
                        style="background-color: rgba(0,0,0,.5) !important;"
                      >
                        <v-avatar left size="10" :style="swatchStyle(item)"></v-avatar>
                        {{ item?.meta?.color || 'None' }}
                      </v-chip>
                    </template>

                    <v-card class="pa-2" width="260">
                      <div class="d-flex flex-wrap">
                        <v-btn
                          v-for="(key, idx) in colorOrder"
                          :key="key"
                          class="ma-1"
                          small
                          depressed
                          :style="{
                            backgroundColor: palette[key],
                            minWidth: '34px',
                            height: '34px'
                          }"
                          @click="setRowColorLocal(item, key)"
                          :title="key"
                        >
                          <span v-if="(item?.meta?.color || null) !== key" class="swatch-num">
                            {{ idx + 1 }}
                          </span>
                          <v-icon
                            small
                            v-if="(item?.meta?.color || null) === key"
                            class="swatch-check"
                          >
                            mdi-check
                          </v-icon>
                        </v-btn>

                        <v-btn class="ma-1" small outlined @click="setRowColorLocal(item, 'default')">
                          Clear
                        </v-btn>
                      </div>
                    </v-card>
                  </v-menu>
                </div>
              </div>

              <div class="sales-group">
                <v-chip label dark :color="salesGroup(item)?.color || 'default'">
                  <v-icon left>{{ salesGroup(item)?.icon }}</v-icon>
                  <span>{{ salesGroup(item)?.label }}</span>
                </v-chip>
              </div>
            </template>

            <!-- fallback -->
            <template v-else>
              {{ item[h.value] }}
            </template>
          </td>
        </tr>
      </draggable>
    </template>
  </v-data-table>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "OrdersTable",
  components: { draggable },
  props: {
    headers: { type: Array, required: true },
    items: { type: Array, required: true },
    loading: { type: Boolean, default: false },

    selectedIds: { type: Array, default: () => [] },

    colorOrder: { type: Array, default: () => [] },
    palette: { type: Object, default: () => ({}) },

    onSaveText: { type: Function, required: true },
  },
  data() {
    return {
        expandedItems: {},
        itemsWithOverflow: {},
        colorMenus: {},

        isEditingText: null,
        editingText: "",
        options: {
            page: 1,
            itemsPerPage: 10,
            sortBy: [],
            sortDesc: [],
            groupBy: [],
            groupDesc: [],
            multiSort: false,
            mustSort: false,
        },
    };
  },
  computed: {
    selectedIdsProxy: {
      get() {
        return this.selectedIds;
      },
      set(v) {
        this.$emit("update:selectedIds", v);
      },
    },
  },
  watch: {
    items() {
        this.queueOverflowCheckVisible();
    },
    loading(val) {
        if (!val) this.queueOverflowCheckVisible();
    },
    options: {
        handler() {
        this.queueOverflowCheckVisible();
        },
        deep: true,
    },
  },
  mounted() {
    this.queueOverflowCheckVisible();
  },
  methods: {
    onDragEnd(pageItems) {
      this.$emit("reorder-page", pageItems);
      this.queueOverflowCheckVisible();
    },

    queueOverflowCheckVisible() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const keys = Object.keys(this.$refs || {}).filter(k => k.startsWith('items-'));

          for (const k of keys) {
            const ref = this.$refs[k];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (!el) continue;

            const idStr = k.replace('items-', '');
            const id = Number.isFinite(Number(idStr)) ? Number(idStr) : idStr;


            const hasOverflow = el.scrollHeight > el.clientHeight;


            this.$set(this.itemsWithOverflow, id, hasOverflow);
          }
        });
      });
    },

    checkOverflowById(itemId) {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const ref = this.$refs[`items-${itemId}`];
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (!el) return;

          this.$set(this.itemsWithOverflow, itemId, el.scrollHeight > el.clientHeight);
        });
      });
    },

    toggleExpand(itemId) {
      this.$set(this.expandedItems, itemId, !this.expandedItems[itemId]);
    },

    editText(item) {
      this.isEditingText = item.id;
      this.editingText = (item.items_text || "").trim()
        ? item.items_text
        : this.itemsToText(item?.items || []);
    },

    cancelTextEdit() {
      this.isEditingText = null;
      this.editingText = "";
    },

    async saveText(id) {
      try {
        await this.onSaveText(id, this.editingText);
        this.cancelTextEdit();
        this.$nextTick(() => requestAnimationFrame(() => this.queueOverflowCheckVisible()));
      } catch (e) {
        console.log("SAVE TEXT ERROR:", e);
      }
    },

    itemsToText(items) {
      return items.map((i) => this.itemToLine(i)).filter(Boolean).join("\n");
    },

    itemToLine(i) {
      const qty = i.qty ?? i.num_of_pcs ?? 0;
      const def_qty = i.default_qty ?? 0;
      const unit = i.unit ?? "db";
      const ref = i.reference ?? "";
      const name = i.item_name ?? "";

      const parts = [
        `${qty} db`,
        ref && `- ${ref}`,
        name && `- ${name}`,
        def_qty && `- ${def_qty} ${unit}`,
      ].filter(Boolean);

      return parts.join(" ");
    },

    hasShipping(item) {
      const s = item && item.shipping_address && item.shipping_address.shipping;
      return !!(s && (s.postal_code || s.city || s.address || s.country));
    },

    shippingLine1(item) {
      const s = item.shipping_address.shipping;
      const pc = s.postal_code ? String(s.postal_code) : "";
      const city = s.city ? `${s.city},` : "";
      return `${pc} ${city}`.trim();
    },

    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString();
    },

    formatCountry(country) {
      const c = (country || "").trim();
      if (!c) return "";
      return c === "HU" || c === "Hungary" || c === "HUN" ? "" : c;
    },

    setRowColorLocal(item, key) {
      if (!item.meta) this.$set(item, "meta", {});
      this.$set(item.meta, "color", key || "default");
      this.$set(this.colorMenus, item.id, false);

      this.$emit("color-change", item, key || "default");
    },

    swatchStyle(item) {
      const key = item?.meta?.color;
      const hex = key ? this.palette[key] : "#E5E7EB";
      return { backgroundColor: hex };
    },

    rowStyle(item) {
      if (this.isDueWithinDays(item, 5)) {
        return { backgroundColor: this.hexToRgba(this.palette.red, 0.10) };
      }

      const key = item?.meta?.color ?? "default";
      const hex = this.palette[key];
      if (!hex) return {};
      return { backgroundColor: this.hexToRgba(hex, 0.10) };
    },

    hexToRgba(hex, alpha = 0.1) {
      const h = String(hex || "").replace("#", "");
      if (h.length !== 6) return `rgba(0,0,0,${alpha})`;

      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    parseDeliveryDate(v) {
      if (!v) return null;

      const m = String(v).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
      if (!m) return null;

      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10);
      const d = parseInt(m[3], 10);

      return new Date(y, mo - 1, d);
    },

    isDueWithinDays(item, days = 5) {
      const target = this.parseDeliveryDate(item?.meta?.delivery_date);
      if (!target) return false;

      const today = new Date();
      const utcDay = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      const diffDays = Math.round((utcDay(target) - utcDay(today)) / 86400000);

      return Math.abs(diffDays) <= days;
    },

    salesGroup(item) {
      const sp = Number(item?.meta?.sales_point_id ?? 0);

      if ([2, 5, 6, 9].includes(sp)) {
        return { label: "Livestock", icon: "mdi-cow", color: "teal" };
      }
      if (sp === 7) {
        return { label: "Ironmongery", icon: "mdi-wrench", color: "blue-grey" };
      }
      return { label: "Other", icon: "mdi-help-circle-outline", color: "grey" };
    },
  },
};
</script>

<style>

.item-tr{
  height: 100% !important;
  max-height: 135px !important;
  overflow: hidden !important;
  transition: .3s all ease-in;
  align-content: center;
}
.item-tr.expanded{
  height: 100% !important;
  max-height: 100% !important;
  padding: 1em 0;
  transition: .3s all ease;
}

.expand-icon {
  cursor: pointer;
  transition: transform 0.3s ease, rotate 0.3s ease;
}
.expand-icon:hover {
  transform: scale(1.2);
}

.ghost-row {
  opacity: 0.5;
  background: #f5f5f5;
}

.dragging-row {
  background: #e3f2fd;
  cursor: grabbing !important;
}

td {
  position: relative;
}

tr.completed td::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 2px;
  background-color: black;
  transform: translate(-50%, -50%);
  transition: width 0.5s ease-in-out;
  pointer-events: none;
}

tr.completed.active td::before {
  width: 100%;
}

tr.completed td::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(200,200,200,.5);
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  pointer-events: none;
}

tr.completed.active td::after {
  opacity: 1;
}

tr a{
  transition: all .5s ease;
}
tr a:hover {
  color: darkblue;
  transition: .5s all ease;
  font-size: 15px;
}

.items-text{
  white-space: pre-line;
  word-break: break-word;
}
.comment-text{
  white-space: pre-line;
  word-break: break-word;
}

td:has(.edit-button){
  position: relative;
}
.edit-button{
  transition: .3s all ease;
  position: absolute !important;
  right: 5px !important;
  top: 10px !important;
}
.expand-icon.edit-button{
  position: relative;
  right: unset !important;
  top: unset !important;
}
.edit-button:hover{
  color: rgb(200, 145, 0) !important;
  transition: .3s all ease;
}

.textarea-buttons{
  position: absolute !important;
  right: 20px !important;
  bottom: 5px !important;
}
.check-button,.cancel-button{
  transition: .3s all ease;
}
.check-button:hover{
  color: rgb(95, 205, 0) !important;
  transform: translateY(-2px);
  transition: .3s all ease;
}
.cancel-button:hover{
  color: rgb(185, 0, 0) !important;
  transform: translateY(-2px);
  transition: .3s all ease;
}

td .v-textarea, td .v-textarea *:not(.v-input__append-inner) {
  height: 100%;
}

.administrator{
  padding: 0.5em;
  border-radius: 5px;
  cursor: help;
  background: rgba(25,118,210,.12) !important;
  color: rgba(25,118,210,1) !important;
  border: 1px solid rgba(25,118,210,.35) !important;
}

.swatch-num{
  font-size: 12px;
  font-weight: 700;
  color: rgba(0,0,0,0.75);
  text-shadow: 0 1px 1px rgba(255,255,255,0.45);
}

.swatch-check{
  position: absolute;
  top: 2px;
  right: 2px;
  color: rgba(0,0,0,0.85);
}

.sales-group{
  position: absolute;
  top: 0;
  right: 0;
}
.sales-group .v-chip{
  border-radius: 0 !important;
}

.hover-rows:hover{
  background-color: rgba(0,0,0,.05) !important;
}
</style>
